import { defineStore } from "pinia";

import attributesApi from "@/services/api/attributesApi";
import edoDocumentsApi from "@/services/api/edoDocumentsApi";

import { getCompanyId } from "@/utils/userUtils";
import { useNotificationStore } from "@/stores/notificationStore";

// Совпадает с TTL backend lock: если worker исчез без terminal-события,
// интерфейс не должен оставаться в состоянии синхронизации навсегда.
const SYNC_PROGRESS_STALE_AFTER_MS = 40 * 60 * 1000;

export const useEdoDocumentStore = defineStore("edoDocumentStore", {
  state: () => ({
    documents: [],
    selectedDocument: null,
    companyId: getCompanyId(),
    isLoading: false,
    isSubmitting: false,
    error: null,
    lastSyncedAt: null,
    // По integration_id: { status, total, processed }, см. EdoDocumentSyncChannel
    syncProgress: {},

    pagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
    },
  }),

  getters: {
    isSyncing: (state) => Object.keys(state.syncProgress).length > 0,
  },

  actions: {
    applyDocumentsResponse(response) {
      this.documents = response.data.result || [];

      const meta = response.data.pagination_meta;
      if (meta) {
        this.pagination = {
          page: meta.page,
          pages: meta.pages,
          series: meta.series,
          next: meta.next,
          prev: meta.prev,
          count: meta.count,
          limit: meta.limit,
        };
      }
    },

    clearDocuments() {
      this.documents = [];
      this.pagination = {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      };
    },

    applyUpdatedDocument(updated) {
      if (!updated) return;

      this.documents = this.documents.map((document) =>
        document.id === updated.id ? updated : document
      );
      if (this.selectedDocument?.id === updated.id) {
        this.selectedDocument = updated;
      }
    },

    // Загружает список документов ЭДО и обновляет pagination
    async fetchDocuments(params = {}) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await edoDocumentsApi.index(this.companyId, params);
        this.applyDocumentsResponse(response);
        return { documents: this.documents, pagination: this.pagination };
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Загружает документ для страницы деталей с общим loader
    async fetchDocument(id) {
      this.isLoading = true;
      this.selectedDocument = null;
      try {
        const response = await edoDocumentsApi.show(this.companyId, id);
        this.selectedDocument = response.data.result || response.data;
        return this.selectedDocument;
      } finally {
        this.isLoading = false;
      }
    },

    // Тихо перечитывает документ без loader, используется для polling PDF
    async fetchDocumentSilently(id) {
      const response = await edoDocumentsApi.show(this.companyId, id);
      const document = response.data.result || response.data;
      this.applyUpdatedDocument(document);
      return document;
    },

    async fetchDuplicatePayload(documentId) {
      const response = await edoDocumentsApi.duplicate(this.companyId, documentId);
      return response.data.result || response.data;
    },

    async updateInternalDetails(documentId, payload) {
      this.isSubmitting = true;
      try {
        const response = await edoDocumentsApi.updateInternalDetails(
          this.companyId,
          documentId,
          payload
        );
        const document = response.data.result || response.data;
        this.applyUpdatedDocument(document);
        useNotificationStore().showSuccess("Рабочие данные документа сохранены");
        return document;
      } finally {
        this.isSubmitting = false;
      }
    },

    async archiveDocument(documentId) {
      this.isSubmitting = true;
      try {
        const response = await edoDocumentsApi.archive(this.companyId, documentId);
        const document = response.data.result || response.data;
        this.applyUpdatedDocument(document);
        useNotificationStore().showSuccess("Документ архивирован");
        return document;
      } finally {
        this.isSubmitting = false;
      }
    },

    async restoreDocument(documentId) {
      this.isSubmitting = true;
      try {
        const response = await edoDocumentsApi.restore(this.companyId, documentId);
        const document = response.data.result || response.data;
        this.applyUpdatedDocument(document);
        useNotificationStore().showSuccess("Документ восстановлен");
        return document;
      } finally {
        this.isSubmitting = false;
      }
    },

    // Ставит фоновую синхронизацию из Saby, не дожидаясь её завершения.
    // Реальный прогресс приходит отдельно через EdoDocumentSyncChannel.
    async syncDocuments({ integrationId, dateFrom } = {}) {
      if (!integrationId) throw new Error("Выберите интеграцию Saby");
      this.error = null;
      try {
        const response = await edoDocumentsApi.sync(this.companyId, integrationId, dateFrom);
        (response.data.result || []).forEach((entry) => {
          this.syncProgress = {
            ...this.syncProgress,
            [entry.integration_id]: {
              status: entry.status,
              total: null,
              processed: null,
              updatedAt: Date.now(),
            },
          };
        });
        return true;
      } catch (error) {
        this.error = error;
        throw error;
      }
    },

    // Обновляет прогресс одной интеграции из сообщения EdoDocumentSyncChannel
    applySyncProgress({ integration_id: integrationId, status, total, processed }) {
      if (status === "finished" || status === "failed") {
        const nextProgress = { ...this.syncProgress };
        delete nextProgress[integrationId];
        this.syncProgress = nextProgress;
        return;
      }

      this.syncProgress = {
        ...this.syncProgress,
        [integrationId]: { status, total, processed, updatedAt: Date.now() },
      };
    },

    // Убирает sync, от которого за время lease не пришёл ни один статус.
    // Возвращает integration_id, чтобы UI мог сообщить пользователю об остановке.
    expireStaleSyncProgress(now = Date.now()) {
      const expiredIntegrationIds = Object.entries(this.syncProgress)
        .filter(([, progress]) => (
          progress.updatedAt && now - progress.updatedAt >= SYNC_PROGRESS_STALE_AFTER_MS
        ))
        .map(([integrationId]) => Number(integrationId));

      if (expiredIntegrationIds.length === 0) return [];

      const nextProgress = { ...this.syncProgress };
      expiredIntegrationIds.forEach((integrationId) => {
        delete nextProgress[integrationId];
      });
      this.syncProgress = nextProgress;

      return expiredIntegrationIds;
    },

    // Получает время последней успешной синхронизации отдельным легким запросом
    async fetchLastSyncedAt() {
      const response = await edoDocumentsApi.lastSyncedAt(this.companyId);
      this.lastSyncedAt = response.data.result || null;
      return this.lastSyncedAt;
    },

    // Создаёт ЭТрН в выбранной интеграции и кладёт новый документ в начало списка
    async buildTitle1Document(integrationId, documentData, sourceLinks = {}, mode = "send") {
      if (mode === "send" && !integrationId) throw new Error("Выберите интеграцию Saby");
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.buildTitle1(
          this.companyId, integrationId, documentData, sourceLinks, mode
        );
        const document = response.data.result;
        if (document) {
          this.documents = [document, ...this.documents.filter((d) => d.id !== document.id)];
        }
        useNotificationStore().showSuccess(mode === "draft" ? "Черновик ЭТрН сохранён" : "ЭТрН создана");
        return document;
      } finally {
        this.isLoading = false;
      }
    },

    // Создаёт заказ-заявку на перевозку в выбранной интеграции через backend TransportOrder
    async buildTransportOrderDocument(integrationId, documentData, sourceLinks = {}, mode = "send") {
      if (mode === "send" && !integrationId) throw new Error("Выберите интеграцию Saby");
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.buildTransportOrder(
          this.companyId, integrationId, documentData, sourceLinks, mode
        );
        const document = response.data.result;
        if (document) {
          this.documents = [document, ...this.documents.filter((d) => d.id !== document.id)];
        }
        useNotificationStore().showSuccess(mode === "draft" ? "Черновик заказ-заявки сохранён" : "Заказ-заявка создана");
        return document;
      } finally {
        this.isLoading = false;
      }
    },

    // Создаёт поручение экспедитору в выбранной Saby-интеграции
    async buildForwarderOrderDocument(integrationId, documentData, sourceLinks = {}, mode = "send") {
      if (mode === "send" && !integrationId) throw new Error("Выберите интеграцию Saby");
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.buildForwarderOrder(
          this.companyId, integrationId, documentData, sourceLinks, mode
        );
        const document = response.data.result;
        if (document) {
          this.documents = [document, ...this.documents.filter((d) => d.id !== document.id)];
        }
        useNotificationStore().showSuccess(mode === "draft" ? "Черновик поручения экспедитору сохранён" : "Поручение экспедитору создано");
        return document;
      } finally {
        this.isLoading = false;
      }
    },

    // Создаёт экспедиторскую расписку в выбранной Saby-интеграции
    async buildForwarderReceiptDocument(integrationId, documentData, sourceLinks = {}, mode = "send") {
      if (mode === "send" && !integrationId) throw new Error("Выберите интеграцию Saby");
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.buildForwarderReceipt(
          this.companyId, integrationId, documentData, sourceLinks, mode
        );
        const document = response.data.result;
        if (document) {
          this.documents = [document, ...this.documents.filter((d) => d.id !== document.id)];
        }
        useNotificationStore().showSuccess(mode === "draft" ? "Черновик расписки сохранён" : "Экспедиторская расписка создана");
        return document;
      } finally {
        this.isLoading = false;
      }
    },

    // Создаёт локальный черновик из XML: backend парсит файл и заполняет parsed_data
    async importDocumentXml({ documentType, integrationId, xmlBase64, sourceLinks = {} }) {
      if (!integrationId) throw new Error("Выберите интеграцию Saby");
      if (!xmlBase64) throw new Error("Выберите XML-файл");

      this.isSubmitting = true;
      try {
        const response = await edoDocumentsApi.importXml(this.companyId, {
          document_type: documentType,
          integration_id: integrationId,
          xml_base64: xmlBase64,
          order_id: sourceLinks.orderId,
          trip_id: sourceLinks.tripId,
        });
        const document = response.data.result;
        if (document) {
          this.documents = [document, ...this.documents.filter((d) => d.id !== document.id)];
        }
        useNotificationStore().showSuccess("Черновик создан из XML");
        return document;
      } finally {
        this.isSubmitting = false;
      }
    },

    // Отправляет локальный черновик документа в Saby через выбранную интеграцию
    async submitToProvider(documentId, integrationId) {
      if (!integrationId) throw new Error("Выберите интеграцию Saby");
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.submitToProvider(this.companyId, documentId, integrationId);
        const document = response.data.result;
        if (document) {
          this.documents = this.documents.map((d) => (d.id === document.id ? document : d));
          if (this.selectedDocument?.id === document.id) this.selectedDocument = document;
        }
        useNotificationStore().showSuccess("Документ отправлен в Saby");
        return document;
      } finally {
        this.isLoading = false;
      }
    },

    // Обновляет документ из Saby и синхронизирует selectedDocument
    async refreshDocument(documentId) {
      const response = await edoDocumentsApi.refresh(this.companyId, documentId);
      const updated = response.data.result;
      this.applyUpdatedDocument(updated);
      return updated;
    },

    // Запускает backend-проверку XML-вложения документа
    async validateDocumentXml(documentId) {
      const response = await edoDocumentsApi.validateXml(this.companyId, documentId);
      return response.data.result;
    },

    // Запрашивает workflow ЭЗЗ из Saby и обновляет текущую карточку
    async diagnoseTransportOrderWorkflow(documentId) {
      const response = await edoDocumentsApi.diagnoseWorkflow(this.companyId, documentId);
      const updated = response.data.result;
      this.applyUpdatedDocument(updated);
      return updated;
    },

    // Сохраняет изменения формы ЭЗЗ: backend пересобирает XML и отправляет обновление в Saby
    async updateTransportOrderForm(documentId, documentData) {
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.updateForm(this.companyId, documentId, documentData);
        const updated = response.data.result;
        this.applyUpdatedDocument(updated);
        return updated;
      } finally {
        this.isLoading = false;
      }
    },

    // Меняет только локальные привязки ЭДО-документа к заказу и рейсу.
    async updateDocumentSourceLinks(documentId, sourceLinks) {
      this.isSubmitting = true;
      try {
        const response = await edoDocumentsApi.updateSourceLinks(
          this.companyId,
          documentId,
          sourceLinks
        );
        const updated = response.data.result;
        this.applyUpdatedDocument(updated);
        useNotificationStore().showSuccess("Связи документа обновлены");
        return updated;
      } finally {
        this.isSubmitting = false;
      }
    },

    // Сохраняет титул 2 ЭЗЗ: данные перевозчика, ТС и водителя пишутся отдельным XML 1110362
    async updateTransportOrderTitle2(documentId, documentData) {
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.updateTitle2(this.companyId, documentId, documentData);
        const updated = response.data.result;
        this.applyUpdatedDocument(updated);
        return updated;
      } finally {
        this.isLoading = false;
      }
    },

    // Сохраняет титул экспедитора поручения: backend собирает XML 1110487
    async updateForwarderOrderTitle2(documentId, documentData) {
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.updateForwarderOrderTitle2(
          this.companyId, documentId, documentData
        );
        const updated = response.data.result;
        this.applyUpdatedDocument(updated);
        useNotificationStore().showSuccess("Титул экспедитора сохранён");
        return updated;
      } finally {
        this.isLoading = false;
      }
    },

    // Сохраняет титул клиента поручения: backend пересобирает XML 1110486
    async updateForwarderOrderTitle1(documentId, documentData) {
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.updateForwarderOrderTitle1(
          this.companyId, documentId, documentData
        );
        const updated = response.data.result;
        this.applyUpdatedDocument(updated);
        useNotificationStore().showSuccess("Поручение экспедитору сохранено");
        return updated;
      } finally {
        this.isLoading = false;
      }
    },

    // Сохраняет экспедиторскую расписку: backend пересобирает XML 1110489
    async updateForwarderReceiptTitle1(documentId, documentData) {
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.updateForwarderReceiptTitle1(
          this.companyId, documentId, documentData
        );
        const updated = response.data.result;
        this.applyUpdatedDocument(updated);
        useNotificationStore().showSuccess("Экспедиторская расписка сохранена");
        return updated;
      } finally {
        this.isLoading = false;
      }
    },

    // Сохраняет титул 2 ЭТрН: факт приёмки груза перевозчиком пишется XML 1110340
    async updateConsignmentNoteTitle2(documentId, documentData) {
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.updateTitle2(this.companyId, documentId, documentData);
        const updated = response.data.result;
        this.applyUpdatedDocument(updated);
        useNotificationStore().showSuccess("Титул 2 ЭТрН сохранён");
        return updated;
      } finally {
        this.isLoading = false;
      }
    },

    // Сохраняет титул 3 ЭТрН: результат приёмки грузополучателем пишется XML 1110341
    async updateConsignmentNoteTitle3(documentId, documentData) {
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.updateTitle3(this.companyId, documentId, documentData);
        const updated = response.data.result;
        this.applyUpdatedDocument(updated);
        useNotificationStore().showSuccess("Титул 3 ЭТрН сохранён");
        return updated;
      } finally {
        this.isLoading = false;
      }
    },

    // Сохраняет титул 4 ЭТрН: выдача груза перевозчиком пишется XML 1110342
    async updateConsignmentNoteTitle4(documentId, documentData) {
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.updateTitle4(this.companyId, documentId, documentData);
        const updated = response.data.result;
        this.applyUpdatedDocument(updated);
        useNotificationStore().showSuccess("Титул 4 ЭТрН сохранён");
        return updated;
      } finally {
        this.isLoading = false;
      }
    },

    // Сохраняет Титул 7 ЭТрН 1167009: переадресовку после титула 2
    async updateConsignmentNoteRedirect(documentId, documentData) {
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.updateRedirect(this.companyId, documentId, documentData);
        const updated = response.data.result;
        this.applyUpdatedDocument(updated);
        useNotificationStore().showSuccess("Титул 7 ЭТрН сохранён");
        return updated;
      } finally {
        this.isLoading = false;
      }
    },

    // Сохраняет дополнительный сценарий ЭТрН 1110344: замену водителя или ТС после титула 2
    async updateConsignmentNoteReplacement(documentId, documentData) {
      this.isLoading = true;
      try {
        const response = await edoDocumentsApi.updateReplacement(this.companyId, documentId, documentData);
        const updated = response.data.result;
        this.applyUpdatedDocument(updated);
        useNotificationStore().showSuccess("Замена водителя/ТС сохранена");
        return updated;
      } finally {
        this.isLoading = false;
      }
    },

    // Загружает подписи полей карточки ЭДО-документа из настроек атрибутов
    async fetchDocumentAttributeLabels() {
      const response = await attributesApi.renderAttributes(
        this.companyId,
        { record_type: "Edo::Document" }
      );
      return response.data?.parsed_attrs || [];
    },

    // Скачивает файл документа через API-слой, чтобы компонент не работал с axios напрямую
    async downloadDocumentFile(url) {
      return edoDocumentsApi.downloadFile(url);
    },

    // Скачивает PDF ЭДО через backend, чтобы preview не зависел от CORS storage-домена
    async downloadDocumentPdf(documentId) {
      return edoDocumentsApi.downloadPdf(this.companyId, documentId);
    },

  },
});
